import type { AdditionalDetailsData } from '@adyen/adyen-web';
import {
	type ActorRefFrom,
	assertEvent,
	assign,
	fromPromise,
	setup,
	type StateFrom,
} from 'xstate';

import { isError } from 'errors';
import { useEngagementGoals } from 'hooks';
import { useProfilingGTM, usePurchaseGTM } from 'hooks/gtm';
import {
	useDeleteSearchParams,
	useNavigation,
	useSearchParams,
} from 'hooks/navigation';
import type { Cart, CartErrorType, VerifyPaymentRequest } from 'models/api';
import { makeImplementationWarning } from 'state-machines/helpers';
import type { CartId } from 'types';
import type { CheckoutUpdatableCartProperties } from 'utils/business-logic';
import { arrayUnique } from 'utils/collection';
import { isClient } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import Mutex from 'utils/mutex';

import {
	checkoutCart,
	copyCart,
	fetchNewCart,
	fetchSpecificCart,
	updateCart,
	verifyRedirectPayment,
	waitForUserInfoActor,
} from './checkout.actors';
import type {
	CheckoutMachineContext,
	CheckoutMachineEvents,
	PaymentStateError,
} from './checkout.types';
import { deliveryMachine } from './delivery/delivery.machine';
import { giftCardBonusMachine } from './giftCardBonus/giftCardBonus.machine';
import { usePaymentMachine } from './payment/payment.machine';
import {
	userInfoMachine,
	type UserInfoMachineActor,
} from './userInfo/userInfo.machine';

/**
 * Request mutex
 * - used to make the updatedCart request synchronous.
 */
const requestMutex = new Mutex();

export const checkoutMachine = setup({
	// oxlint-disable-next-line typescript/no-unnecessary-type-assertion
	types: {} as {
		context: CheckoutMachineContext;
		events: CheckoutMachineEvents;
	},
	actions: {
		sendToGTM: makeImplementationWarning('sendToGTM'),
		sendEngagement: makeImplementationWarning('sendEngagement'),
		spawnChildMachines: makeImplementationWarning('spawnChildMachines'),
		setErrorListCheckoutErrorMessages: assign({
			checkoutErrorMessages: ({ context }) => {
				const errorKeys: CartErrorType[] = [
					'CartIsEmpty',
					'ContactReferenceNotSelected',
					'AddressFirstNameInvalidLength',
					'AddressFirstNameInvalidChar',
					'AddressLastNameInvalidLength',
					'AddressLastNameInvalidChar',
					'AddressAddressInvalidLength',
					'AddressAddressInvalidChar',
					'AddressCoAddressInvalidLength',
					'AddressCoAddressInvalidChar',
					'AddressPostalCodeInvalidLength',
					'AddressPostalCodeInvalid',
					'AddressCityInvalidLength',
					'AddressCityInvalidChar',
					'DeliveryMethodInvalid',
					'DeliveryMethodNotSelected',
					'DiscountCombinationInvalid',
					'EmailInvalid',
					'EmailInvalidForCustomer',
					'PaymentMethodInvalid',
					'PaymentMethodNotSelected',
					'PickupLocationInvalid',
					'PickupLocationNotSelected',
					'TelephoneNumberInvalid',
					'CompanyIdentificationNumberInvalidChar',
					'CompanyIdentificationNumberInvalidLength',
					'CompanyNameInvalidChar',
					'CompanyNameInvalidLength',
					'VatRegistrationNumberInvalidChar',
					'VatRegistrationNumberInvalidLength',
					'EmptyCustomerAddress',
				];

				return (
					context.data?.errorList
						?.filter((error) => error.type && errorKeys.includes(error.type))
						.map((error) => error.description) ?? []
				);
			},
		}),
		setPaymentErrorMessages: makeImplementationWarning<{
			error: PaymentStateError;
		}>('setPaymentErrorMessages'),
		setPaymentFailedErrorMessages: makeImplementationWarning(
			'setPaymentFailedErrorMessages',
		),
		setShowCheckoutValidNotificationMessage: makeImplementationWarning(
			'setShowCheckoutValidNotificationMessage',
		),
		notifyChildrenCartIsUpdated: ({ context }) => {
			context.deliveryActor.send({
				type: 'CART_UPDATED',
				value: context.data,
			});
			context.paymentActor.send({
				type: 'CART_UPDATED',
				value: context.data,
			});
			context.userInfoActor.send({
				type: 'CART_UPDATED',
				value: context.data,
			});
			context.giftCardBonusActor.send({
				type: 'CART_UPDATED',
				value: context.data,
			});
		},
		initPayment: ({ context }) => {
			context.paymentActor.send({
				type: 'INIT_PAYMENT',
			});
		},
		checkPaymentState: ({ context }) => {
			context.paymentActor.send({
				type: 'CHECK_PAYMENT_STATE',
			});
		},
		setVerifyPaymentDataFromRedirectUrl: makeImplementationWarning(
			'setVerifyPaymentDataFromRedirectUrl',
		),
		removeVerifyPaymentRedirectFromUrl: makeImplementationWarning(
			'removeVerifyPaymentRedirectFromUrl',
		),
		setAdditionalDetailsData: assign({
			verifyPaymentData: (
				_,
				params: { data: AdditionalDetailsData | undefined },
			) => ({
				verifyPayment: true,
				// TODO: This mapping used to be done in Payment component.
				// Temporarly moved here since payment details prop is linked to
				// payment request params. Look at making the context prop 'freeer',
				//and map to the data where the request is done.
				paymentDetails: {
					details: {
						redirectResult: params?.data?.data.details.payload,
					},
					paymentData: params?.data?.data.paymentData,
				},
			}),
		}),
		resetVerifyPaymentData: assign({
			verifyPaymentData: () => undefined,
		}),
		setIdFromRedirectResultUrl: makeImplementationWarning(
			'setIdFromRedirectResultUrl',
		),

		setData: assign({
			data: (_, params: { cart: Cart | undefined }) => params.cart,
			cartId: (_, params: { cart: Cart | undefined }) => params.cart?.id,
		}),

		notifyChildToReset: ({ context }) => {
			context.paymentActor.send({
				type: 'RESET_TO_OLD',
			});
			context.deliveryActor.send({
				type: 'RESET_TO_OLD',
			});
		},

		setPayButtonStateToSuccess: assign({
			payButtonState: () => 'success' as const,
		}),
		setPayButtonStateToFailed: assign({
			payButtonState: () => 'failure' as const,
		}),
		setPayButtonStateToLoading: assign({
			payButtonState: () => 'loading' as const,
		}),

		setOrderData: assign({
			orderData: (_, params: { cart: Cart }) => params.cart,
			data: () => undefined,
		}),

		notifyPaymentMachineToFinalizePayment: ({ context }) => {
			context.paymentActor.send({
				type: 'FINALIZE_PAYMENT',
			});
		},
		showNotifications: assign({
			showNotification: () => true,
		}),
		resetShowNotifications: assign({
			showNotification: () => false,
		}),
		resetFailedLoadingRetries: assign({
			failedLoadingRetries: () => 0,
		}),
		incFailedLoadingRetries: assign({
			failedLoadingRetries: ({ context }) => context.failedLoadingRetries + 1,
		}),
		removeCartIdFromUrl: makeImplementationWarning('removeCartIdFromUrl'),
		goToOrderConfirmationPage: makeImplementationWarning(
			'goToOrderConfirmationPage',
		),
		setShouldInitCheckout: assign({
			shouldInitCheckout: () => true,
		}),
		unSetShouldInitCheckout: assign({
			shouldInitCheckout: () => false,
		}),
	},
	guards: {
		isForbiddenCart: (_, params: { error: unknown }) => {
			if (isError(params.error, 'ResponseError')) {
				return params.error.status === 403;
			}
			return false;
		},
		isClient: () => isClient(),
		shouldSetUrlVerifyPaymentRedirectData: makeImplementationWarning(
			'shouldSetUrlVerifyPaymentRedirectData',
		),
		shouldSetRedirectCartId: makeImplementationWarning(
			'shouldSetRedirectCartId',
		),
		shouldVerifyPayment: ({ context }) => Boolean(context.verifyPaymentData),
		checkoutInvalid: ({ context }) =>
			context.data?.checkoutStatus === 'Invalid',
		cartIsValidButHasDiff: ({ context }) =>
			context.data?.checkoutStatus === 'Valid',
		readyToInitCheckout: ({ context }) =>
			context.data?.checkoutStatus !== 'Invalid',
		hasCartId: ({ context }) => Boolean(context.cartId),
		retriedToManyTimes: ({ context }) => context.failedLoadingRetries >= 6,
		shouldRetryRequest: ({ context }) => context.failedLoadingRetries < 6,
		orderFinalize: ({ context }) =>
			context.data?.checkoutStatus === 'Finalized',
		shouldFinalizeOrder: ({ context }) =>
			context.data?.checkoutStatus === 'Paid' ||
			context.data?.checkoutStatus === 'OrderCreated',
		paymentFailed: ({ context }) =>
			context.data?.checkoutStatus === 'PaymentFailed',
		waitingForPayment: ({ context }) =>
			context.data?.checkoutStatus === 'PaymentPending',
		shouldInitiateCheckout: ({ context }) => context.shouldInitCheckout,
		maybeTemporaryError: (_, params: { error: unknown }) => {
			if (isError(params.error, 'ResponseError')) {
				return params.error.status === 503 || params.error.status === 500;
			}
			return false;
		},
	},
	actors: {
		updateCart: fromPromise(
			async ({
				input: { data, updatedCartProperties },
			}: {
				input: {
					data: Cart | undefined;
					updatedCartProperties: CheckoutUpdatableCartProperties | undefined;
				};
			}) =>
				requestMutex.dispatch(() => updateCart(data, updatedCartProperties)),
		),
		checkoutCart: fromPromise(
			({ input: { data } }: { input: { data: Cart | undefined } }) =>
				checkoutCart(data),
		),
		copyCart: fromPromise(
			({ input: { cartId } }: { input: { cartId: CartId | undefined } }) =>
				copyCart(cartId),
		),
		getCartWithPost: fromPromise(() => fetchNewCart()),
		getCartWithId: fromPromise(
			({ input: { cartId } }: { input: { cartId: CartId | undefined } }) =>
				fetchSpecificCart(cartId),
		),
		verifyPayment: fromPromise(
			({
				input: { cartId, paymentDetails },
			}: {
				input: {
					cartId: CartId | undefined;
					paymentDetails: VerifyPaymentRequest | undefined;
				};
			}) => verifyRedirectPayment(cartId, paymentDetails),
		),
		waitForUserActor: fromPromise(
			({
				input: { userInfoActor },
			}: {
				input: { userInfoActor: UserInfoMachineActor };
			}) => waitForUserInfoActor(userInfoActor),
		),
	},
	delays: {
		RETRY_DELAY: ({ context }) => (context.failedLoadingRetries || 1) * 2000,
	},
}).createMachine({
	context: {
		// Child actors should technically be nullable but they're created on
		// entry so pretend they're not to keep usage simpler. Using ts-ignore
		// will disable typing for every context property so use any instead.
		// oxlint-disable-next-line typescript/no-explicit-any
		paymentActor: null as any,
		// oxlint-disable-next-line typescript/no-explicit-any
		deliveryActor: null as any,
		// oxlint-disable-next-line typescript/no-explicit-any
		userInfoActor: null as any,
		// oxlint-disable-next-line typescript/no-explicit-any
		giftCardBonusActor: null as any,
		showLogin: false,
		payButtonState: 'idle',
		failedLoadingRetries: 0,
		showNotification: false,
		shouldInitCheckout: false,
		checkoutErrorMessages: [],
	},

	id: 'checkoutMachine',
	initial: 'initialization',
	states: {
		initialization: {
			description:
				'This state is run on startup, here wait for the auth machine to finish, set data about the user provided by the auth machine, and if we have been redirected from a payment provider we set data from the url',
			initial: 'startUpChildMachines',
			states: {
				startUpChildMachines: {
					description:
						'Entry state, spawn children, set errors if any, unfortunately we also need to check that we have a window object since the guards are "run" on server side as well and are dependent on the window',
					entry: ['spawnChildMachines', 'setErrorListCheckoutErrorMessages'],
					always: { target: 'checkUrl', guard: 'isClient' },
				},
				checkUrl: {
					description:
						'Check if we have been redirect from a payment provider and set that data from the url',
					always: [
						{
							target: '#loadCart',
							description:
								'If we have been redirected from a payment provider we set the data from the url',
							guard: 'shouldSetUrlVerifyPaymentRedirectData',
							actions: [
								'setVerifyPaymentDataFromRedirectUrl',
								'removeVerifyPaymentRedirectFromUrl',
							],
						},
						{
							target: '#loadCart',
							guard: 'shouldSetRedirectCartId',
							description:
								'If a user has pressed the back button from a payment provider we set the cartId from the url to get back where we were',
							actions: 'setIdFromRedirectResultUrl',
						},
						{
							target: '#loadCart',
						},
					],
				},
			},
		},
		loadCart: {
			id: 'loadCart',
			description:
				'When auth is done we load the cart with different methods depending on how the user got here',
			initial: 'determineHowToLoadCart',
			states: {
				determineHowToLoadCart: {
					description:
						'Decide which method to use for load the cart (the order in always is important!)',
					always: [
						{ target: 'verifyPayment', guard: 'shouldVerifyPayment' },
						{ target: 'getCartWithId', guard: 'hasCartId' },
						{ target: 'getCartWithPost' },
					],
				},
				verifyPayment: {
					id: 'verifyPayment',
					description:
						'When returning from a redirect payment with id and payload we load the cart with verifyPayment',
					exit: 'resetVerifyPaymentData',
					initial: 'requestVerifyPayment',
					states: {
						requestVerifyPayment: {
							id: 'requestVerifyPayment',
							invoke: {
								id: 'verifyPayment',
								src: 'verifyPayment',
								input: ({ context }) => ({
									cartId: context.cartId,
									paymentDetails: context.verifyPaymentData?.paymentDetails,
								}),
								onDone: {
									target: '#checkingCartStatus',
									actions: [
										{
											type: 'setData',
											params: ({ event }) => ({ cart: event.output }),
										},
										'setErrorListCheckoutErrorMessages',
									],
								},
								onError: [
									{
										guard: 'shouldVerifyPayment',
										target: 'retry',
									},
									{
										target: '#checkoutMachine.error',
									},
								],
							},
						},
						retry: {
							description: 'If a request has failed, we retry 5 times',
							always: [
								{
									target: '#error',
									guard: 'retriedToManyTimes',
									actions: ['resetFailedLoadingRetries'],
								},
							],
							after: {
								2000: {
									target: '#verifyPayment',
									actions: ['incFailedLoadingRetries'],
								},
							},
						},
					},
				},
				getCartWithId: {
					id: 'getCartWithId',
					description:
						'Load a specific cart with an id, can be the current cart that we need to reload or an id from localStorage or the url',
					invoke: {
						id: 'getCartWithId',
						src: 'getCartWithId',
						input: ({ context }) => ({ cartId: context.cartId }),
						onDone: {
							target: 'checkingCartStatus',
							actions: [
								{
									type: 'setData',
									params: ({ event }) => ({ cart: event.output }),
								},
								'setErrorListCheckoutErrorMessages',
							],
						},
						onError: [
							{
								guard: {
									type: 'isForbiddenCart',
									params: ({ event }) => ({ error: event.error }),
								},
								target: 'forbiddenCart',
							},
							{
								target: '#error',
							},
						],
					},
				},
				forbiddenCart: {
					tags: 'hasForbiddenCartError',
					entry: 'removeCartIdFromUrl',
					type: 'final',
					description:
						'This is a final state since we cannot recover. We need to remove the cartId from the url to avoid ending up here again.',
				},
				getCartWithPost: {
					description:
						'If we dont have any id we load the cart with a POST (which gives us a new cart or the current users last used cart)',
					invoke: {
						src: 'getCartWithPost',
						id: 'getCartWithPost',
						onDone: {
							target: 'checkingCartStatus',
							actions: [
								{
									type: 'setData',
									params: ({ event }) => ({ cart: event.output }),
								},
								'setErrorListCheckoutErrorMessages',
							],
						},
						onError: {
							target: '#error',
						},
					},
				},
				checkingCartStatus: {
					id: 'checkingCartStatus',
					description:
						'Checks the status of the newly loaded cart to decide what to do next',
					entry: 'notifyChildrenCartIsUpdated',
					always: [
						{
							target: '#loadCart.getCartWithPost',
							actions: ['goToOrderConfirmationPage'],
							guard: 'orderFinalize',
						},
						{
							target: '#idle',
							guard: 'shouldFinalizeOrder',
							actions: ['notifyPaymentMachineToFinalizePayment'],
						},
						{
							target: '#copyCart',
							guard: 'paymentFailed',
							actions: [
								'setPaymentFailedErrorMessages',
								'showNotifications',
								'setPayButtonStateToFailed',
							],
						},
						{
							target: 'pollCart',
							guard: 'waitingForPayment',
						},
						{
							target: '#idle',
						},
					],
				},
				pollCart: {
					description:
						'If we have a PaymentPending we poll the cart until finished.',
					after: {
						4000: { target: 'getCartWithId' },
					},
				},
			},
			on: {
				ABORT_PAYMENT_PENDING: {
					target: 'copyCart',
					actions: [
						'setPaymentFailedErrorMessages',
						'showNotifications',
						'removeCartIdFromUrl',
					],
				},
			},
		},
		copyCart: {
			description:
				'If payment failed or any other error after requesting checkout we need to copy the cart to be able to make adjustments to it',
			id: 'copyCart',
			invoke: {
				id: 'copyCart',
				src: 'copyCart',
				input: ({ context }) => ({ cartId: context.cartId }),
				onDone: {
					actions: {
						type: 'setData',
						params: ({ event }) => ({ cart: event.output }),
					},
					target: '#idle',
				},
				onError: {
					description:
						'If get get 500 type error we assume server has issues and check if we should try again',
					target: 'error',
					guard: {
						type: 'maybeTemporaryError',
						params: ({ event }) => ({ error: event.error }),
					},
				},
			},
		},
		updateCart: {
			id: 'updateCart',
			description: 'Update the cart with new data',
			on: {
				INIT_CHECKOUT: {
					actions: ['setPayButtonStateToLoading', 'setShouldInitCheckout'],
				},
				UPDATE_CART: {
					target: 'updateCart',
				},
			},
			invoke: {
				id: 'updateCart',
				src: 'updateCart',
				input: ({ context, event }) => {
					assertEvent(event, 'UPDATE_CART');
					return {
						data: context.data,
						updatedCartProperties: event.updatedCartProperties,
					};
				},
				onDone: [
					{
						actions: [
							{
								type: 'setData',
								params: ({ event }) => ({ cart: event.output }),
							},
							'setErrorListCheckoutErrorMessages',
						],
						target: '#idle',
					},
				],
				onError: [
					{
						actions: ['notifyChildToReset'],
						target: '#error',
					},
				],
			},
		},
		error: {
			id: 'error',
			description:
				'General error state that holds retry logic, if we have an Id we do copy cart, since that endpoint can handle both if we have a cart that is ok or not',
			entry: ['setPayButtonStateToFailed', 'incFailedLoadingRetries'],
			initial: 'checkFailedRetries',
			states: {
				checkFailedRetries: {
					always: {
						target: 'retry',
						guard: 'shouldRetryRequest',
					},
				},
				retry: {
					after: {
						RETRY_DELAY: [
							{
								target: '#copyCart',
								guard: 'hasCartId',
							},
							{
								target: '#loadCart',
							},
						],
					},
				},
			},
			on: {
				UPDATE_CART: {
					target: 'updateCart',
				},
			},
		},
		idle: {
			id: 'idle',
			entry: 'notifyChildrenCartIsUpdated',
			exit: 'unSetShouldInitCheckout',
			always: {
				target: '#checkoutAndPaymentFlow',
				guard: 'shouldInitiateCheckout',
			},
			on: {
				INIT_CHECKOUT: {
					target: '#checkoutAndPaymentFlow',
				},
				UPDATE_CART: {
					target: 'updateCart',
				},
				PAYMENT_DONE: {
					target: '#paymentDone',
					actions: {
						type: 'setOrderData',
						params: ({ event }) => ({ cart: event.orderData }),
					},
				},
			},
		},
		checkoutAndPaymentFlow: {
			id: 'checkoutAndPaymentFlow',
			description: 'We are in checkout state',
			initial: 'prepareForCheckout',
			states: {
				prepareForCheckout: {
					description:
						'We set the payment button to loading and wait for any request still being in progess from userInfo, then we initiating checkout.',
					entry: ['setPayButtonStateToLoading', 'resetShowNotifications'],
					invoke: {
						id: 'waitForUserInfoActor',
						src: 'waitForUserActor',
						input: ({ context }) => ({
							userInfoActor: context.userInfoActor,
						}),
						onDone: [
							{
								target: 'determinePaymentState',
								guard: 'readyToInitCheckout',
							},
							{
								target: 'checkoutAndPaymentFlowError',
							},
						],
						onError: [
							{
								target: 'checkoutAndPaymentFlowError',
							},
						],
					},
					on: {
						UPDATE_CART: {
							actions: 'setShouldInitCheckout',
							target: '#updateCart',
						},
					},
				},
				determinePaymentState: {
					entry: 'checkPaymentState',
					description:
						'The payment machine holds the state for payment types with internal state, we need to check that these are ok before we proceed and initate checkout to not get a locked cart',
					on: {
						PAYMENT_STATE_ERROR: {
							target: 'checkoutAndPaymentFlowError',
							actions: [
								'setPayButtonStateToFailed',
								{
									type: 'setPaymentErrorMessages',
									params: ({ event }) => ({ error: event.error }),
								},
							],
						},
						PAYMENT_STATE_OK: {
							target: 'initiatingCheckout',
						},
					},
				},
				initiatingCheckout: {
					description:
						'User has clicked the buy-button we start the checkout process',
					invoke: {
						id: 'checkoutCart',
						src: 'checkoutCart',
						input: ({ context }) => ({ data: context.data }),
						onDone: {
							target: 'determineCheckoutState',
							actions: [
								{
									type: 'setData',
									params: ({ event }) => ({ cart: event.output }),
								},
								'notifyChildrenCartIsUpdated',
								'setErrorListCheckoutErrorMessages',
							],
						},
						onError: [
							{
								target: 'checkoutAndPaymentFlowError',
							},
						],
					},
				},
				determineCheckoutState: {
					always: [
						{
							description:
								'There is still something invalid with the checkoutstatus that was not caught bu the check before we initiate checkout, perhaps something more needs to happend than just going to checkoutError',
							guard: 'checkoutInvalid',
							target: 'checkoutAndPaymentFlowError',
						},
						{
							description:
								'Status is valid for checkout, but api detects change between this request and last PUT request and wants to notify user of this change and requires us to prompt them to INIT_CHECKOUT again',
							guard: 'cartIsValidButHasDiff',
							target: 'checkoutAndPaymentFlowError',
							actions: ['setShowCheckoutValidNotificationMessage'],
						},
						{
							target: 'initiatingPayment',
						},
					],
				},
				initiatingPayment: {
					entry: 'initPayment',
					tags: 'initiatingPayment',
					description:
						'Initiate the payment by telling the payment actor to do so and wait for it to be done',
					on: {
						CHECK_CHECKOUT_STATUS: {
							target: '#loadCart',
						},
						PAYMENT_DONE: {
							target: 'paymentDone',
							actions: {
								type: 'setOrderData',
								params: ({ event }) => ({ cart: event.orderData }),
							},
						},
					},
				},
				paymentDone: {
					id: 'paymentDone',
					entry: ['goToOrderConfirmationPage'],
					description:
						'Payment is done, we load the cart to get the latest details',
					always: [
						{
							target: '#checkoutMachine.loadCart.getCartWithPost',
							actions: [
								'sendToGTM',
								'sendEngagement',
								'setPayButtonStateToSuccess',
							],
						},
					],
				},
				checkoutAndPaymentFlowError: {
					description: 'Error state',
					always: [
						{
							target: '#idle',
							actions: ['setPayButtonStateToFailed', 'showNotifications'],
						},
					],
					on: {
						UPDATE_CART: {
							target: '#updateCart',
						},
					},
				},
			},
		},
	},
	on: {
		// TODO: There is a receiveAdditionalDetails that is sent to the payment machine that in turns sends this
		// when it gets it, maybe that flow can be simplified or made clearer
		receiveAdditionalDetails: {
			target: '#loadCart',
			actions: {
				type: 'setAdditionalDetailsData',
				params: ({ event }) => ({ data: event.data }),
			},
		},
		PAYMENT_FAILED: {
			target: '#copyCart',
			actions: ['setPayButtonStateToFailed'],
		},
		RESET_SHOW_NOTIFICATION: {
			actions: 'resetShowNotifications',
		},
	},
});
export type CheckoutMachineState = StateFrom<typeof checkoutMachine>;
export type CheckoutMachineActor = ActorRefFrom<typeof checkoutMachine>;

export function useCheckoutMachine() {
	const { sendPurchaseEvent } = usePurchaseGTM();
	const { sendProfilingPurchaseEvent } = useProfilingGTM();
	const { sendEngagement } = useEngagementGoals();
	const { t } = useI18n();

	const searchParams = useSearchParams();

	const { deleteSearchParams } = useDeleteSearchParams();
	const { navigate } = useNavigation();
	const paymentMachine = usePaymentMachine();

	return checkoutMachine.provide({
		guards: {
			shouldSetUrlVerifyPaymentRedirectData: () =>
				searchParams.hasValue('verifyRedirectPayment'),
			shouldSetRedirectCartId: () => searchParams.hasValue('cartId'),
		},
		actions: {
			// The payment machine is spawned here instead of with the default
			// action to give it router access, since child machines can't be
			// reached by provide.
			spawnChildMachines: assign({
				deliveryActor: ({ self, spawn }) =>
					spawn(deliveryMachine, { input: { checkoutMachineRef: self } }),
				paymentActor: ({ self, spawn }) =>
					spawn(paymentMachine, { input: { checkoutMachineRef: self } }),
				userInfoActor: ({ self, spawn }) =>
					spawn(userInfoMachine, { input: { checkoutMachineRef: self } }),
				giftCardBonusActor: ({ self, spawn }) =>
					spawn(giftCardBonusMachine, {
						input: { checkoutMachineRef: self },
					}),
			}),
			removeCartIdFromUrl: () => {
				deleteSearchParams('cartId');
			},
			setVerifyPaymentDataFromRedirectUrl: assign({
				verifyPaymentData: () => ({
					verifyPayment: true,
					// Some redirect payments don't have a redirectResult, then the
					// payment is verified without any details.
					paymentDetails: searchParams.hasValue('redirectResult')
						? {
								details: { redirectResult: searchParams.get('redirectResult') },
							}
						: undefined,
				}),
				cartId: () => searchParams.get('cartId'),
			}),
			removeVerifyPaymentRedirectFromUrl: () => {
				deleteSearchParams(['redirectResult', 'verifyRedirectPayment']);
			},
			setIdFromRedirectResultUrl: assign({
				cartId: () => searchParams.get('cartId'),
			}),
			goToOrderConfirmationPage: ({ context }) => {
				const id = context.orderData?.id || context.data?.id;
				navigate(
					{ location: 'orderConfirmation', query: { orderId: id } },
					{ replace: true },
				);
			},
			sendToGTM: ({ context }) => {
				if (!context.orderData?.basketRows) return;
				sendPurchaseEvent(context.orderData);
				sendProfilingPurchaseEvent(context.orderData.basketRows);
			},
			sendEngagement: ({ context }) => {
				const total = context.orderData?.totalSummary.incVat.find(
					(sum) => sum.sumType === 'Total',
				);

				sendEngagement({
					type: 'completePurchase',
					data: {
						total: total?.value ?? 0,
					},
				});
			},
			setShowCheckoutValidNotificationMessage: assign({
				checkoutErrorMessages: ({ context }) => [
					...context.checkoutErrorMessages,
					t('checkout_valid_but_has_diff_notification_text'),
				],
			}),
			setPaymentErrorMessages: assign({
				checkoutErrorMessages: (
					{ context },
					params: { error: PaymentStateError },
				) => {
					// TODO: Same keys and similar logic in quotation,
					// make something reusable?
					const errorMessages: Record<PaymentStateError, string> = {
						BlikPaymentInfoError: t('checkout_blik_payment_info_error_text'),
						CardPaymentInfoError: t('checkout_cardpayment_info_error_text'),
						GeneralPaymentInfoError: t(
							'checkout_general_payment_info_error_text',
						),
					};
					return arrayUnique([
						...context.checkoutErrorMessages,
						errorMessages[params.error],
					]);
				},
			}),
			setPaymentFailedErrorMessages: assign({
				checkoutErrorMessages: ({ context }) => [
					...context.checkoutErrorMessages,
					t('checkout_payment_failed_text'),
				],
			}),
		},
	});
}
